import { useEffect } from "react";
import Cookies from "js-cookie";
import { useNavigate, useParams } from "react-router-dom";

import "./ShipmentTrackingV2.scss";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import usePermissions from "hooks/usePermissions";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetTrackingUpdates } from "api/queries/shipments/useGetTrackingUpdates";
import Activity from "./components/Activity/Activity";
import CostDetails from "./components/CostDetails/CostDetails";
import ScheduleContainer from "./components/Schedule/ScheduleContainer";
import Tracking from "./components/Tracking/Tracking";
import Overview from "./components/Overview/Overview";
import PodAttachmentsCard from "./components/PodAttachmentsCard/PodAttachmentsCard";
import InvolvedParties from "./components/InvolvedParties/InvolvedParties";

interface ShipmentTrackingV2Props {
  /**
   * Whether the shipment is a quick request.
   */
  isQuickRequest?: boolean;
  /**
   * The shipment ID.
   */
  shipmentId?: string;
  /**
   * Whether the shipment is off-platform.
   */
  isOffPlatform: boolean;
}

function ShipmentTrackingV2(props: ShipmentTrackingV2Props) {
  const { isQuickRequest, shipmentId, isOffPlatform } = props;

  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [isCustomBroker] = useCustomBrokerPermission();
  const userType = JSON.parse(permissions ?? "{}").user_type;

  // Get shipment data
  const id = isQuickRequest ? shipmentId! : useParams().id!.toUpperCase(); // If this is a quick request, use the shipmentId passed in. Otherwise, use the id from the URL.
  const {
    data: shipmentDetails = {},
    isLoading: isShipmentDetailsLoading,
    error,
    refetch: refetchShipmentDetails,
  } = useGetShipmentDetails(
    id,
    Cookies.get("is_guest") !== "true", // Don't update the notification setting if the user is a guest
    {
      enabled: !!id,
    },
  );
  const { data: trackingUpdatesData, isLoading: isLoadingTrackingUpdates } = useGetTrackingUpdates(id, { enabled: !!id });

  // If user is unauthorized to view tracking, redirect to quote-request page
  const isUnauthorized = (error as any)?.response?.status === 403;

  // Permissions
  const hasViewShipmentsPermission = JSON.parse(permissions ?? "{}").permissions?.tracking?.view_shipments;
  const hasViewShipmentPricingPermission = JSON.parse(permissions ?? "{}").permissions?.tracking?.view_shipment_pricing;

  // check for guest user
  const isGuestUser = Cookies.get("is_guest") === "true" || shipmentDetails?.viewing_as_guest_user;

  // Check if user has view shipments permission
  useEffect(() => {
    if (Cookies.get("is_guest") === "true") return; // Guest users do not have the permissions object
    if (!hasViewShipmentsPermission) {
      navigate("/access-denied", { replace: true });
      return;
    }
    if (isUnauthorized) {
      navigate(`/shipment/${id}`, { replace: true });
    }
  }, []);

  const renderPodAttachmentsCard = () => (
    <PodAttachmentsCard
      shipmentId={id}
      shipmentDetails={shipmentDetails}
      isQuickRequest={Boolean(isQuickRequest)}
      isGuestUser={Boolean(isGuestUser)}
      isCustomBroker={Boolean(isCustomBroker)}
      isLoading={isShipmentDetailsLoading}
    />
  );

  const renderOverview = () => (
    <Overview
      shipment={shipmentDetails}
      route={trackingUpdatesData?.route}
      isShipmentLoading={isShipmentDetailsLoading}
      isRouteLoading={isLoadingTrackingUpdates}
      isOffPlatform={isOffPlatform}
      onBookingReferenceUpdated={refetchShipmentDetails}
    />
  );
  const renderTracking = () => <Tracking id={id} shipment={shipmentDetails} isShipmentLoading={isShipmentDetailsLoading} />;
  const renderSchedule = () =>
    !isOffPlatform && (
      <ScheduleContainer shipmentDetails={shipmentDetails} isShipmentDetailsLoading={isShipmentDetailsLoading} />
    );
  const renderCostDetails = () =>
    (hasViewShipmentPricingPermission || userType === "provider") &&
    !isOffPlatform &&
    !isCustomBroker &&
    !isGuestUser && <CostDetails shipment={shipmentDetails} isLoading={isShipmentDetailsLoading} />;
  const renderInvolvedParties = () => (
    <InvolvedParties shipmentDetails={shipmentDetails} isOffPlatform={isOffPlatform} isLoading={isShipmentDetailsLoading} />
  );
  const renderActivity = () => <Activity shipmentId={id} shipmentDetails={shipmentDetails} isOffPlatform={isOffPlatform} />;

  if ((error as any)?.response?.status === 403) return null;

  return (
    <>
      <div className="shipment-tracking-v2-container">
        <div>
          {renderTracking()}
          {renderActivity()}
        </div>
        <div>
          {renderPodAttachmentsCard()}
          {renderOverview()}
          {renderSchedule()}
          {renderCostDetails()}
          {renderInvolvedParties()}
        </div>
      </div>

      <div className="shipment-tracking-v2-container--mobile">
        {renderPodAttachmentsCard()}
        {renderOverview()}
        {renderTracking()}
        {renderSchedule()}
        {renderCostDetails()}
        {renderInvolvedParties()}
        {renderActivity()}
      </div>
    </>
  );
}

ShipmentTrackingV2.defaultProps = {
  isQuickRequest: false,
  shipmentId: undefined,
};

export default ShipmentTrackingV2;
